import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { adminTenantSettingsAPI } from '../services/adminApi';

const Panel = styled.div`
  display: grid;
  gap: 1.25rem;
  @media (min-width: 960px) {
    grid-template-columns: minmax(280px, 380px) minmax(0, 1fr);
    align-items: start;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.35rem;
`;

const Help = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 0.65rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.12);
  }
`;

const SaveButton = styled.button`
  margin-top: 1rem;
  background: #d52b1e;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 1.1rem;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PreviewTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
`;

const PreviewShell = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  background: #fff;
`;

const PreviewHeader = styled.div`
  background: linear-gradient(135deg, #d52b1e 0%, #043663 50%, #032a4d 100%);
  color: white;
  padding: 1.25rem;
  text-align: center;
`;

const PreviewBanner = styled.div`
  margin: 0.75rem;
  padding: 0.85rem;
  border-radius: 8px;
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const PreviewBody = styled.div`
  padding: 1rem 1.1rem 1.25rem;
`;

const PreviewField = styled.div`
  margin-bottom: 0.75rem;
  label {
    display: block;
    font-size: 0.8rem;
    font-weight: 600;
    color: #334155;
    margin-bottom: 0.25rem;
  }
  div {
    height: 36px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #f8fafc;
  }
`;

const PreviewPrice = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  text-align: center;
`;

const PreviewPriceValue = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  color: #d52b1e;
`;

const PreviewPriceDesc = styled.div`
  margin-top: 0.35rem;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
`;

/**
 * Admin editor + live preview for registration page banner line and price description.
 */
const AdminRegistrationPageSettings = () => {
  const queryClient = useQueryClient();
  const [bannerExtraLine, setBannerExtraLine] = useState('');
  const [priceDescription, setPriceDescription] = useState('');

  const { data, isLoading } = useQuery(
    ['adminRegistrationPageSettings'],
    async () => {
      const res = await adminTenantSettingsAPI.getRegistrationPage();
      return res?.data || res;
    }
  );

  useEffect(() => {
    if (!data) return;
    setBannerExtraLine(data.bannerExtraLine ?? '');
    setPriceDescription(data.priceDescription ?? '');
  }, [data]);

  const saveMutation = useMutation(
    () =>
      adminTenantSettingsAPI.updateRegistrationPage({
        bannerExtraLine,
        priceDescription,
      }),
    {
      onSuccess: (res) => {
        const payload = res?.data || res;
        queryClient.setQueryData(['adminRegistrationPageSettings'], payload);
        toast.success('Registration page settings saved');
      },
      onError: (e) => {
        toast.error(e.response?.data?.message || e.message || 'Save failed');
      },
    }
  );

  const previewBanner = bannerExtraLine;
  const previewPrice = priceDescription;

  if (isLoading) {
    return <Card>Loading registration page settings…</Card>;
  }

  return (
    <Panel>
      <Card>
        <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.15rem', color: '#0f172a' }}>
          Registration page settings
        </h2>
        <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#64748b', lineHeight: 1.45 }}>
          These lines appear on the player team registration page. Changes apply immediately after save
          (no phase republish needed).
        </p>

        <div style={{ marginBottom: '1rem' }}>
          <Label htmlFor="reg-banner-extra">Header banner extra line</Label>
          <TextArea
            id="reg-banner-extra"
            value={bannerExtraLine}
            onChange={(e) => setBannerExtraLine(e.target.value)}
            placeholder={data?.defaults?.bannerExtraLine || ''}
          />
          <Help>Shown under the phase dates in the green/status banner (e.g. “National finals in August”).</Help>
        </div>

        <div>
          <Label htmlFor="reg-price-desc">Price description</Label>
          <TextArea
            id="reg-price-desc"
            value={priceDescription}
            onChange={(e) => setPriceDescription(e.target.value)}
            placeholder={data?.defaults?.priceDescription || ''}
          />
          <Help>Shown under the registration price at the bottom of the form.</Help>
        </div>

        <SaveButton type="button" disabled={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
          {saveMutation.isLoading ? 'Saving…' : 'Save registration page settings'}
        </SaveButton>
      </Card>

      <Card>
        <PreviewTitle>Live preview</PreviewTitle>
        <PreviewShell>
          <PreviewHeader>
            <div style={{ fontSize: '1.35rem', fontWeight: 700 }}>Join Helvetic Padel Series</div>
            <div style={{ opacity: 0.9, fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Phase preview — registration form
            </div>
          </PreviewHeader>
          <PreviewBanner>
            <div style={{ fontWeight: 600 }}>Registration is open until 31/08/2026, 09:00 (Swiss time)</div>
            <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', opacity: 0.9 }}>
              Phase example • 01/09/2026 - 30/11/2026
            </div>
            {previewBanner ? (
              <div style={{ marginTop: '0.35rem', fontWeight: 500 }}>{previewBanner}</div>
            ) : null}
          </PreviewBanner>
          <PreviewBody>
            <PreviewField>
              <label>Team Name</label>
              <div />
            </PreviewField>
            <PreviewField>
              <label>Canton</label>
              <div />
            </PreviewField>
            <PreviewPrice>
              <PreviewPriceValue>CHF 120</PreviewPriceValue>
              <PreviewPriceDesc>{previewPrice}</PreviewPriceDesc>
            </PreviewPrice>
            <div
              style={{
                marginTop: '0.85rem',
                background: '#d52b1e',
                color: '#fff',
                textAlign: 'center',
                padding: '0.75rem',
                borderRadius: 8,
                fontWeight: 700,
              }}
            >
              Register Team
            </div>
          </PreviewBody>
        </PreviewShell>
      </Card>
    </Panel>
  );
};

export default AdminRegistrationPageSettings;
